document.addEventListener('DOMContentLoaded', () => {
  const section = document.querySelector('[data-scroll-tabs]');
  if (!section) return;

  // ─── Target resolver ──────────────────────────────────────────────────────
  // Resolve a tab's target to its element. The Reviews tab points at the
  // `pdp-social-reviews` section (#pdp-anchor-reviews); if that section isn't
  // on the page, fall back to the standalone `.review-section`.
  const REVIEWS_ANCHOR = 'pdp-anchor-reviews';
  const resolveTarget = (targetId) =>
    document.getElementById(targetId) ??
    (targetId === REVIEWS_ANCHOR ? document.querySelector('.review-section') : null);

  // ─── Tab visibility setup ─────────────────────────────────────────────────
  const tabs = [...section.querySelectorAll('[data-tab-btn]')];

  tabs.forEach((tab) => {
    if (!resolveTarget(tab.dataset.target)) {
      tab.hidden = true;
    }
  });

  const visibleTabs = tabs.filter((tab) => !tab.hidden);
  if (visibleTabs.length === 0) return;

  const anchorIds = visibleTabs.map((tab) => tab.dataset.target).filter(Boolean);
  if (anchorIds.length === 0) return;

  const firstTabId = visibleTabs[0].dataset.target;
  let currentActiveId = firstTabId;

  // ─── Click lock ───────────────────────────────────────────────────────────
  // When a tab is clicked, we programmatically scroll toward its target. If the
  // target sits near the page bottom, the browser clamps the scroll and the
  // target never reaches the tab bar — scroll-spy would then highlight whatever
  // bottom-most section is visible (e.g. Reviews) instead of the clicked tab.
  // While locked, we keep the clicked tab active and ignore scroll-spy; a
  // user-initiated scroll (touch/wheel) releases the lock.
  let clickLocked = false;

  // ─── Set which tab is visually active ────────────────────────────────────
  const setActiveTab = (activeId) => {
    if (activeId === currentActiveId && activeId !== firstTabId) return;
    currentActiveId = activeId;

    tabs.forEach((tab) => {
      if (tab.hidden) return;
      const isActive = tab.dataset.target === activeId;
      tab.dataset.active = isActive ? 'true' : 'false';

      if (isActive) {
        // Center the active tab in the horizontal track. `block: 'nearest'`
        // keeps the fixed tab bar's vertical position untouched (no page jump).
        tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    });
  };

  // First tab active on init
  setActiveTab(firstTabId);

  // ─── Active-tab detection ─────────────────────────────────────────────────
  // Primary: the section straddling the tab bar (top ≤ tabsBottom, bottom > tabsBottom).
  // Fallback: at the very bottom of the page where the last section's bottom
  //           has also passed the tab bar — keep the closest one active.
  const updateActiveTab = () => {
    const tabsBottom = section.getBoundingClientRect().bottom;

    let activeId = null;
    let maxTop   = -Infinity;

    anchorIds.forEach((id) => {
      const el = resolveTarget(id);
      if (!el) return;
      const { top, bottom } = el.getBoundingClientRect();

      if (top <= tabsBottom && bottom > tabsBottom && top > maxTop) {
        maxTop   = top;
        activeId = id;
      }
    });

    if (!activeId) {
      let fallbackMax = -Infinity;
      anchorIds.forEach((id) => {
        const el = resolveTarget(id);
        if (!el) return;
        const top = el.getBoundingClientRect().top;
        if (top <= tabsBottom && top > fallbackMax) {
          fallbackMax = top;
          activeId    = id;
        }
      });
    }

    if (!activeId || activeId === currentActiveId) return;
    setActiveTab(activeId);
  };

  // ─── Scroll: visibility + active tab ─────────────────────────────────────
  window.addEventListener('scroll', () => {
    const atTop = window.scrollY <= 100;
    section.dataset.visible = atTop ? 'false' : 'true';

    if (atTop) {
      if (currentActiveId !== firstTabId) {
        currentActiveId = null;
        setActiveTab(firstTabId);
      }
      return;
    }

    if (clickLocked) return;

    updateActiveTab();
  }, { passive: true });

  // A user-initiated scroll releases the click lock so scroll-spy resumes.
  const releaseClickLock = () => {
    clickLocked = false;
  };
  window.addEventListener('wheel', releaseClickLock, { passive: true });
  window.addEventListener('touchmove', releaseClickLock, { passive: true });

  // ─── Continuous self-correcting smooth scroll ────────────────────────────
  // One single eased animation — no stop-and-jump. Each frame we recompute the
  // target's LIVE document position, so async content (Judge.me widget, lazy
  // images) growing the page mid-scroll is absorbed continuously instead of
  // leaving a stale target that has to be corrected with a second jump.
  // ease-in-out matches the browser's native smooth-scroll feel; duration
  // scales with distance (clamped) so short and long jumps both feel natural.
  // Tuned to match the browser's native smooth-scroll pace (slower on long
  // jumps) — raise MS_PER_PX / MAX_DURATION to slow it further.
  const MIN_DURATION = 400;
  const MAX_DURATION = 1600;
  const MS_PER_PX    = 0.32;

  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);

  // Absolute document Y that puts the target's top just under the tab bar.
  const targetScrollY = (targetEl) =>
    window.scrollY + targetEl.getBoundingClientRect().top - section.getBoundingClientRect().bottom;

  const scrollToTarget = (targetEl) => {
    const startY    = window.scrollY;
    const startTime = performance.now();
    const distance  = Math.abs(targetScrollY(targetEl) - startY);
    const duration  = Math.min(Math.max(distance * MS_PER_PX, MIN_DURATION), MAX_DURATION);

    const step = (now) => {
      if (!clickLocked) return;

      const progress = Math.min((now - startTime) / duration, 1);
      const eased     = easeInOut(progress);

      // Re-read the target every frame so page growth is followed live.
      const nextY = startY + (targetScrollY(targetEl) - startY) * eased;
      window.scrollTo(0, nextY);

      if (progress < 1) requestAnimationFrame(step);
    };

    requestAnimationFrame(step);
  };

  // ─── Tab clicks: smooth-scroll to target ─────────────────────────────────
  visibleTabs.forEach((tab) => {
    tab.addEventListener('click', (e) => {
      e.preventDefault();

      const { target } = tab.dataset;
      if (!target) return;

      const targetEl = resolveTarget(target);
      if (!targetEl) return;

      // Lock the clicked tab active so scroll-spy doesn't override it while we
      // animate, then live-scroll to the target (absorbs async layout growth).
      clickLocked = true;
      currentActiveId = null;
      setActiveTab(target);

      scrollToTarget(targetEl);
    });
  });
});
